import { useAppForm } from "@/components/ui/forms/hooks/useFormHook"
import { errorToastClassName, successToastClassName, appToast as toast } from "@/components/ui/baseUi/sonner"
import { useUser } from "@/modules/users/hooks/useUser"
import { useCurrentBox } from "@modules/box/hooks/useBox"
import { CouponType } from "@modules/common/couponObject/gateway/couponObjectApiDto"
import { word } from "@modules/core/utils/i18n"
import { DEFAULT_MAXIMUM_AMOUNT } from "@modules/currency/currency"
import { useLanguage } from "@modules/language/hooks/useLanguage"
import { BoxPromoCodeEntity } from "@modules/promoCode/entities/boxPromoCodeEntity"
import { PromoCodeEntity } from "@modules/promoCode/entities/promoCodeEntity"
import { useBoxPromoCodesCreate, useBoxPromoCodesUpdate } from "@modules/promoCode/hooks/useBoxPromoCodesActions"
import { usePromoCodesCreate, usePromoCodesUpdate } from "@modules/promoCode/hooks/usePromoCodesActions"
import { usePromoCodeSidebar } from "@modules/promoCode/hooks/usePromoCodeSidebar"
import { defaultDateFormat } from "@modules/utils/dateUtils"
import { isAxiosError } from "axios"
import dayjs from "dayjs"
import { useMemo } from "react"
import { z } from "zod"

function getPromoCodeSchema(isBoxOffer: boolean, stripeMinimumAmount: number, stripeMaximumAmount: number) {
  return z
    .object({
      id: z.string().or(z.undefined()),
      title: z
        .string()
        .min(1, word("global.form.required"))
        .max(40, word("form.maximum.characters", { count: 40 })),
      code: z
        .string()
        .min(1, word("global.form.required"))
        .max(30, word("form.maximum.characters", { count: 30 }))
        .regex(/^[A-Za-z0-9]+$/, word("promoCode.form.input.code.regexError")),
      startAt: z.date().or(z.undefined()),
      type: z.enum(CouponType),
      value: z.number().or(z.null()).or(z.undefined()),
      currency: z.string(),
      maxAttendeesLimited: z.boolean(),
      maxAttendees: z.number().or(z.null()).or(z.undefined()),
      hasExpirationDate: z.boolean(),
      expirationDate: z.date().or(z.undefined()),
      hasDurationLimit: z.boolean(),
      durationInMonths: z.number().or(z.null()).or(z.undefined()),
      onlyNewCustomer: z.boolean(),
      programs: z.array(z.string()),
      programBundles: z.array(z.string()),
      boxOffers: z.array(z.string()),
    })
    .superRefine((values, ctx) => {
      // Validate value based on type
      if (values.type === CouponType.PERCENT) {
        if (values.value === undefined || values.value === null) {
          ctx.addIssue({ code: "custom", message: word("global.form.required"), path: ["value"] })
        } else if (values.value < 1) {
          ctx.addIssue({
            code: "custom",
            message: word("global.form.minimumValue", { minValue: 1 }),
            path: ["value"],
          })
        } else if (values.value > 100) {
          ctx.addIssue({
            code: "custom",
            message: word("global.form.maximumValue", { maxValue: 100 }),
            path: ["value"],
          })
        }
      } else {
        if (values.value === undefined || values.value === null) {
          ctx.addIssue({ code: "custom", message: word("global.form.required"), path: ["value"] })
        } else if (stripeMinimumAmount >= 0 && values.value < stripeMinimumAmount) {
          ctx.addIssue({
            code: "custom",
            message: word("global.form.minimumValue", { minValue: stripeMinimumAmount }),
            path: ["value"],
          })
        } else if (values.value > stripeMaximumAmount) {
          ctx.addIssue({
            code: "custom",
            message: word("global.form.maximumValue", { maxValue: stripeMaximumAmount }),
            path: ["value"],
          })
        }
      }

      // Validate maxAttendees
      if (values.maxAttendeesLimited) {
        if (values.maxAttendees === undefined || values.maxAttendees === null) {
          ctx.addIssue({ code: "custom", message: word("global.form.required"), path: ["maxAttendees"] })
        } else if (values.maxAttendees < 1) {
          ctx.addIssue({
            code: "custom",
            message: word("global.form.minimumValue", { minValue: 1 }),
            path: ["maxAttendees"],
          })
        } else if (values.maxAttendees > 1e5) {
          ctx.addIssue({
            code: "custom",
            message: word("global.form.maximumValue", { maxValue: 1e5 }),
            path: ["maxAttendees"],
          })
        }
      }

      // Validate expirationDate
      if (values.hasExpirationDate && !values.expirationDate) {
        ctx.addIssue({
          code: "custom",
          message: word("global.form.required"),
          path: ["expirationDate"],
        })
      }

      // Validate durationInMonths
      if (values.hasDurationLimit) {
        if (values.durationInMonths === undefined || values.durationInMonths === null) {
          ctx.addIssue({
            code: "custom",
            message: word("global.form.required"),
            path: ["durationInMonths"],
          })
        } else if (values.durationInMonths < 1) {
          ctx.addIssue({
            code: "custom",
            message: word("global.form.minimumValue", { minValue: 1 }),
            path: ["durationInMonths"],
          })
        } else if (values.durationInMonths > 120) {
          ctx.addIssue({
            code: "custom",
            message: word("global.form.maximumValue", { maxValue: 120 }),
            path: ["durationInMonths"],
          })
        }
      }

      // Validate programs / programBundles / boxOffers
      if (isBoxOffer) {
        if (values.boxOffers.length === 0) {
          ctx.addIssue({
            code: "custom",
            message: word("promoCode.form.input.relatedOffersError"),
            path: ["boxOffers"],
          })
        }
      } else {
        if (values.programs.length === 0 && values.programBundles.length === 0) {
          ctx.addIssue({
            code: "custom",
            message: word("promoCode.form.input.relatedProgramsError"),
            path: ["programs"],
          })
          ctx.addIssue({
            code: "custom",
            message: word("promoCode.form.input.relatedProgramsError"),
            path: ["programBundles"],
          })
        }
      }
    })
}

export function usePromoCodeForm(updatedPromoCode?: PromoCodeEntity | BoxPromoCodeEntity) {
  const user = useUser()
  const language = useLanguage()
  const { result: box } = useCurrentBox()
  const { navigateToPromoCodeDetailsView } = usePromoCodeSidebar()
  const { updateBoxPromoCode } = useBoxPromoCodesUpdate()
  const { updatePromoCode } = usePromoCodesUpdate()
  const { createBoxPromoCode } = useBoxPromoCodesCreate()
  const { createPromoCode } = usePromoCodesCreate()

  const isBoxOffer = !!box
  const schema = getPromoCodeSchema(
    isBoxOffer,
    user.stripeMinimumAmount ?? 0,
    user.stripeMaximumAmount ?? DEFAULT_MAXIMUM_AMOUNT,
  )

  const form = useAppForm({
    defaultValues: {
      id: updatedPromoCode?.id,
      title: updatedPromoCode?.title ?? "",
      code: updatedPromoCode?.code ?? "",
      value: (updatedPromoCode?.value ?? undefined) as number | null | undefined,
      currency: box?.stripeAccountCurrency ?? user.stripeAccountCurrency ?? "EUR",
      onlyNewCustomer: updatedPromoCode?.onlyNewCustomer ?? false,
      maxAttendeesLimited: (updatedPromoCode?.maxAttendees ?? 0) !== 0,
      maxAttendees: (updatedPromoCode?.maxAttendees ?? undefined) as number | null | undefined,
      hasExpirationDate: !!updatedPromoCode?.expirationDate,
      expirationDate: updatedPromoCode?.expirationDate ? dayjs(updatedPromoCode.expirationDate).toDate() : undefined,
      hasDurationLimit: !!updatedPromoCode?.durationInMonths,
      durationInMonths: (updatedPromoCode?.durationInMonths ?? undefined) as number | null | undefined,
      type: updatedPromoCode?.type ?? CouponType.PERCENT,
      programs:
        updatedPromoCode && "programs" in updatedPromoCode
          ? updatedPromoCode.programs.map((p) => p.id)
          : ([] as string[]),
      programBundles:
        updatedPromoCode && "programBundles" in updatedPromoCode
          ? updatedPromoCode.programBundles.map((pb) => pb.id)
          : ([] as string[]),
      boxOffers:
        updatedPromoCode && "boxOffers" in updatedPromoCode
          ? updatedPromoCode.boxOffers.map((bo) => bo.id)
          : ([] as string[]),
      startAt: undefined as Date | undefined,
    },
    validators: {
      onSubmit: schema,
    },
    onSubmit: async ({ value, formApi }) => {
      const successToast = () =>
        toast.success(word("promoCode.form.submit.success"), {
          classNames: successToastClassName(),
        })

      const handleError = (e: unknown) => {
        console.error("Error on create/update promoCode", e)
        const serverMessage = isAxiosError(e)
          ? (e.response?.data as { message?: string } | undefined)?.message
          : undefined
        const message = serverMessage ?? (e instanceof Error ? e.message : String(e))

        toast.error(word("global.error.label"), {
          description: message.includes("already exists")
            ? word("promoCode.form.submit.errorDuplicate", { code: value.code })
            : message,
          classNames: errorToastClassName(),
        })
      }

      if (value.id) {
        try {
          if (box) {
            const updated = await updateBoxPromoCode({
              id: value.id,
              promoCodeDraft: {
                title: value.title,
                code: value.code,
                maxAttendees: value.maxAttendees ?? undefined,
                onlyNewCustomer: value.onlyNewCustomer,
                selectedBoxOfferIds: value.boxOffers,
                type: value.type,
                startAt: dayjs(value.startAt).format(defaultDateFormat),
                value: value.value ?? 0,
                createdAt: dayjs().toISOString(),
                expirationDate: value.expirationDate
                  ? dayjs(value.expirationDate).format(defaultDateFormat)
                  : undefined,
                durationInMonths: value.durationInMonths ?? undefined,
              },
            })
            successToast()
            // Reset so the form is pristine and the sidebar onHide confirmation is skipped when navigating away
            formApi.reset()
            navigateToPromoCodeDetailsView(updated, true)
          } else {
            const updated = await updatePromoCode({
              id: value.id,
              promoCodeDraft: {
                title: value.title,
                code: value.code,
                maxAttendees: value.maxAttendees ?? undefined,
                onlyNewCustomer: value.onlyNewCustomer,
                selectedProgramIds: value.programs,
                selectedProgramBundleIds: value.programBundles,
                type: value.type,
                startAt: value.startAt ? dayjs(value.startAt).format(defaultDateFormat) : undefined,
                value: value.value ?? 0,
                createdAt: dayjs().toISOString(),
                expirationDate: value.expirationDate
                  ? dayjs(value.expirationDate).format(defaultDateFormat)
                  : undefined,
                durationInMonths: value.durationInMonths ?? undefined,
              },
            })
            successToast()
            formApi.reset()
            navigateToPromoCodeDetailsView(updated, true)
          }
        } catch (e) {
          handleError(e)
        }
      } else {
        try {
          if (box) {
            const created = await createBoxPromoCode({
              title: value.title,
              code: value.code,
              maxAttendees: value.maxAttendees ?? undefined,
              onlyNewCustomer: value.onlyNewCustomer,
              selectedBoxOfferIds: value.boxOffers,
              type: value.type,
              startAt: dayjs(value.startAt).format(defaultDateFormat),
              value: value.value ?? 0,
              createdAt: dayjs().toISOString(),
              expirationDate: value.expirationDate ? dayjs(value.expirationDate).format(defaultDateFormat) : undefined,
              durationInMonths: value.durationInMonths ?? undefined,
            })
            successToast()
            formApi.reset()
            navigateToPromoCodeDetailsView(created, true)
          } else {
            const created = await createPromoCode({
              title: value.title,
              code: value.code,
              maxAttendees: value.maxAttendees ?? undefined,
              onlyNewCustomer: value.onlyNewCustomer,
              selectedProgramIds: value.programs,
              selectedProgramBundleIds: value.programBundles,
              type: value.type,
              startAt: value.startAt ? dayjs(value.startAt).format(defaultDateFormat) : undefined,
              value: value.value ?? 0,
              createdAt: dayjs().toISOString(),
              expirationDate: value.expirationDate ? dayjs(value.expirationDate).format(defaultDateFormat) : undefined,
              durationInMonths: value.durationInMonths ?? undefined,
            })
            successToast()
            formApi.reset()
            navigateToPromoCodeDetailsView(created, true)
          }
        } catch (e) {
          handleError(e)
        }
      }
    },
  })

  const promoCodeTypesOptions = useMemo(
    () => [
      { value: CouponType.PERCENT, label: word("promoCode.form.input.type.value.percentage") },
      { value: CouponType.DIRECT, label: word("promoCode.form.input.type.value.fixedAmount") },
    ],
    [language],
  )

  const expirationOptions = useMemo(
    () => [
      { value: "donthaveExpirationDate", label: word("promoCode.form.input.expiration.value.unlimited") },
      { value: "hasExpirationDate", label: word("promoCode.form.input.expiration.value.limited") },
    ],
    [language],
  )

  const applicationDurationOptions = useMemo(
    () => [
      { value: "donthaveDurationLimit", label: word("promoCode.form.input.applicationDuration.value.unlimited") },
      { value: "hasDurationLimit", label: word("promoCode.form.input.applicationDuration.value.limited") },
    ],
    [language],
  )

  const maxAttendeesOptions = useMemo(
    () => [
      { value: "maxAttendeesLimited", label: word("promoCode.form.input.maxAttendees.value.limited") },
      { value: "donthaveMaxAttendeesLimited", label: word("promoCode.form.input.maxAttendees.value.unlimited") },
    ],
    [language],
  )

  const onSelectProgram = (id: string) => {
    const current = form.getFieldValue("programs")
    if (current.includes(id)) {
      form.setFieldValue(
        "programs",
        current.filter((pid) => pid !== id),
      )
    } else {
      form.setFieldValue("programs", [...current, id])
    }
  }

  const onSelectAllPrograms = (allIds: string[]) => {
    const current = form.getFieldValue("programs")
    if (allIds.length === current.length) {
      form.setFieldValue("programs", [])
    } else {
      form.setFieldValue("programs", allIds)
    }
  }

  const onSelectProgramBundle = (id: string) => {
    const current = form.getFieldValue("programBundles")
    if (current.includes(id)) {
      form.setFieldValue(
        "programBundles",
        current.filter((pid) => pid !== id),
      )
    } else {
      form.setFieldValue("programBundles", [...current, id])
    }
  }

  const onSelectAllProgramBundles = (allIds: string[]) => {
    const current = form.getFieldValue("programBundles")
    if (allIds.length === current.length) {
      form.setFieldValue("programBundles", [])
    } else {
      form.setFieldValue("programBundles", allIds)
    }
  }

  const onSelectBoxOffer = (id: string) => {
    const current = form.getFieldValue("boxOffers")
    if (current.includes(id)) {
      form.setFieldValue(
        "boxOffers",
        current.filter((bid) => bid !== id),
      )
    } else {
      form.setFieldValue("boxOffers", [...current, id])
    }
  }

  const onSelectAllBoxOffers = (allIds: string[]) => {
    const current = form.getFieldValue("boxOffers")
    if (allIds.length === current.length) {
      form.setFieldValue("boxOffers", [])
    } else {
      form.setFieldValue("boxOffers", allIds)
    }
  }

  return {
    form,
    schema,
    promoCodeTypesOptions,
    expirationOptions,
    applicationDurationOptions,
    maxAttendeesOptions,
    onSelectProgram,
    onSelectAllPrograms,
    onSelectProgramBundle,
    onSelectAllProgramBundles,
    onSelectBoxOffer,
    onSelectAllBoxOffers,
  }
}
